import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { authService } from "../services/auth.service";

export default function LoginForm() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [attempts, setAttempts] = useState(0);
  const navigate = useNavigate();
  const location = useLocation();

  const validateEmail = (email: string) => {
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return emailRegex.test(email) && email.length <= 254;
  };

  useEffect(() => {
    if (authService.isAuthenticated()) {
      const from = location.state?.from?.pathname || "/dashboard";
      navigate(from, { replace: true });
    }
  }, [navigate, location]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email || !password) {
      setError("Preencha todos os campos.");
      return;
    }

    if (!validateEmail(email)) {
      setError("Por favor, insira um e-mail válido.");
      return;
    }

    if (password.length < 3 || password.length > 128) {
      setError("Senha deve ter entre 3 e 128 caracteres.");
      return;
    }

    if (attempts >= 5) {
      setError("Muitas tentativas de login. Tente novamente em alguns minutos.");
      return;
    }

    setLoading(true);
    setError("");

    try {
      await authService.login({ email, password });
      const from = location.state?.from?.pathname || "/dashboard";
      navigate(from, { replace: true });
    } catch (err: any) {
      console.log("Erro de login:", err);
      setAttempts(prev => prev + 1);
      if (err.message.includes("Invalid credentials") || err.message.includes("Credenciais inválidas")) {
        setError("Email ou senha incorretos. Verifique suas credenciais.");
      } else {
        setError(err.message || "Erro ao fazer login");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "linear-gradient(135deg, var(--background) 0%, var(--muted) 100%)",
        padding: 16,
      }}
    >
      <div
        style={{
          width: "100%",
          maxWidth: 900,
          boxShadow: "0 20px 60px rgba(0,0,0,0.15)",
          borderRadius: 20,
          overflow: "hidden",
          background: "var(--card)",
          animation: "slideUp 0.5s cubic-bezier(0.4, 0, 0.2, 1)",
          border: "1px solid var(--border)",
        }}
      >
        <div style={{ display: "flex", flexDirection: "row", minHeight: 650 }}>
          <div
            style={{
              flex: 3,
              background: "linear-gradient(135deg, #f98423 0%, #65b8b5 100%)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              position: "relative",
              padding: 48,
              overflow: "hidden",
            }}
          >
            <div style={{
              textAlign: "center",
              color: "white",
              position: "relative",
              zIndex: 1,
            }}>
              <div style={{ marginBottom: 40 }}>
                <div
                  style={{
                    width: "100%",
                    maxWidth: 340,
                    margin: "0 auto 32px",
                    background: "rgba(255,255,255,0.18)",
                    backdropFilter: "blur(10px)",
                    borderRadius: 28,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexDirection: "column",
                    gap: 16,
                    padding: "28px 24px",
                    boxShadow: "0 8px 32px rgba(0,0,0,0.1)",
                    animation: "float 3s ease-in-out infinite",
                  }}
                >
                  <img
                    src="/logo-horizontal.png"
                    alt="Achou Pet"
                    style={{
                      width: "100%",
                      maxWidth: 240,
                      height: "auto",
                      display: "block",
                    }}
                  />
                  <div
                    style={{
                      fontSize: 12,
                      fontWeight: 700,
                      letterSpacing: "0.16em",
                      textTransform: "uppercase",
                      color: "rgba(255,255,255,0.88)",
                    }}
                  >
                    Painel administrativo
                  </div>
                </div>
              </div>
              <h1 style={{ fontSize: 36, fontWeight: 800, marginBottom: 20, letterSpacing: "-1px" }}>
                Bem-vindo de volta
              </h1>
              <p style={{ fontSize: 18, opacity: 0.9, lineHeight: 1.6, maxWidth: 400, margin: "0 auto" }}>
                Acesse sua conta de administrador com segurança e gerencie todo o sistema
              </p>
            </div>
            <div
              style={{
                position: "absolute",
                top: -60,
                right: -60,
                width: 200,
                height: 200,
                background: "rgba(255,255,255,0.16)",
                borderRadius: "50%",
                animation: "pulse 4s ease-in-out infinite",
              }}
            ></div>
            <div
              style={{
                position: "absolute",
                bottom: -40,
                left: -40,
                width: 160,
                height: 160,
                background: "rgba(255,255,255,0.16)",
                borderRadius: "50%",
                animation: "pulse 4s ease-in-out infinite 1s",
              }}
            ></div>
          </div>

          <div style={{ flex: 2, background: "var(--card)" }}>
            <div
              style={{
                padding: 48,
                height: "100%",
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
              }}
            >
              <div style={{ marginBottom: 40 }}>
                <h2
                  style={{
                    fontSize: 28,
                    fontWeight: 800,
                    color: "var(--card-foreground)",
                    marginBottom: 12,
                    letterSpacing: "-0.5px",
                  }}
                >
                  Login
                </h2>
                <p style={{ color: "var(--muted-foreground)", fontSize: 15 }}>
                  Entre com suas credenciais para continuar
                </p>
              </div>
              <form
                onSubmit={handleSubmit}
                style={{ display: "flex", flexDirection: "column", gap: 24 }}
              >
                <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                  <label
                    htmlFor="email"
                    style={{ color: "var(--card-foreground)", fontWeight: 600, fontSize: 14 }}
                  >
                    E-mail
                  </label>
                  <input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    style={{
                      background: "var(--input)",
                      border: "2px solid var(--border)",
                      borderRadius: 10,
                      padding: "14px 16px",
                      fontSize: 15,
                      transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                      outline: "none",
                    }}
                    placeholder="seu@email.com"
                    autoComplete="username"
                    required
                    onFocus={(e) => {
                      e.currentTarget.style.borderColor = "var(--primary)";
                      e.currentTarget.style.boxShadow = "0 0 0 3px rgba(59, 130, 246, 0.1)";
                    }}
                    onBlur={(e) => {
                      e.currentTarget.style.borderColor = "var(--border)";
                      e.currentTarget.style.boxShadow = "none";
                    }}
                  />
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                  <label
                    htmlFor="password"
                    style={{ color: "var(--card-foreground)", fontWeight: 600, fontSize: 14 }}
                  >
                    Senha
                  </label>
                  <input
                    id="password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    style={{
                      background: "var(--input)",
                      border: "2px solid var(--border)",
                      borderRadius: 10,
                      padding: "14px 16px",
                      fontSize: 15,
                      transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                      outline: "none",
                    }}
                    placeholder="••••••••"
                    autoComplete="current-password"
                    required
                    onFocus={(e) => {
                      e.currentTarget.style.borderColor = "var(--primary)";
                      e.currentTarget.style.boxShadow = "0 0 0 3px rgba(59, 130, 246, 0.1)";
                    }}
                    onBlur={(e) => {
                      e.currentTarget.style.borderColor = "var(--border)";
                      e.currentTarget.style.boxShadow = "none";
                    }}
                  />
                </div>
                {error && (
                  <div
                    style={{
                      color: "#ef4444",
                      fontSize: 14,
                      fontWeight: 500,
                      background: "rgba(239, 68, 68, 0.1)",
                      padding: "12px 16px",
                      borderRadius: 8,
                      border: "1px solid rgba(239, 68, 68, 0.2)",
                    }}
                  >
                    {error}
                  </div>
                )}
                <button
                  type="submit"
                  disabled={loading}
                  style={{
                    width: "100%",
                    background: loading ? "var(--muted)" : "linear-gradient(135deg, var(--primary) 0%, var(--accent) 100%)",
                    color: "white",
                    fontWeight: 700,
                    padding: "14px 24px",
                    border: "none",
                    borderRadius: 10,
                    fontSize: 16,
                    cursor: loading ? "not-allowed" : "pointer",
                    boxShadow: loading ? "none" : "0 4px 14px rgba(59, 130, 246, 0.4)",
                    transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                  }}
                  onMouseEnter={(e) => {
                    if (!loading) {
                      e.currentTarget.style.transform = "translateY(-2px)";
                      e.currentTarget.style.boxShadow = "0 6px 20px rgba(59, 130, 246, 0.5)";
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!loading) {
                      e.currentTarget.style.transform = "translateY(0)";
                      e.currentTarget.style.boxShadow = "0 4px 14px rgba(59, 130, 246, 0.4)";
                    }
                  }}
                >
                  {loading ? "Entrando..." : "Entrar"}
                </button>
                <div style={{ textAlign: "center" }}>
                  <button
                    type="button"
                    style={{
                      fontSize: 14,
                      color: "var(--primary)",
                      background: "none",
                      border: "none",
                      padding: 0,
                      cursor: "pointer",
                      fontWeight: 600,
                      transition: "all 0.2s",
                    }}
                    onClick={() => navigate("/forgot")}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.textDecoration = "underline";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.textDecoration = "none";
                    }}
                  >
                    Esqueceu sua senha?
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>

      <style>
        {`
          @keyframes slideUp {
            from {
              opacity: 0;
              transform: translateY(30px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }

          @keyframes float {
            0%, 100% {
              transform: translateY(0);
            }
            50% {
              transform: translateY(-20px);
            }
          }

          @keyframes pulse {
            0%, 100% {
              transform: scale(1);
              opacity: 0.1;
            }
            50% {
              transform: scale(1.1);
              opacity: 0.15;
            }
          }
        `}
      </style>
    </div>
  );
}
